import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox, PosLayout } from "../../../components";
import useSigWeb, { LCD_H, LCD_W, SIGWEB_CANVAS_H, SIGWEB_CANVAS_W } from "../../../hooks/useSigWeb";

// ── Extra SigWeb LCD/keypad APIs used by this test page (not needed elsewhere) ─
declare global {
  interface Window {
    TabletModelNumber?:     () => number;
    KeyPadClearHotSpotList?: () => void;
    KeyPadAddHotSpot?:      (id: number, mode: number, x: number, y: number, w: number, h: number) => void;
    KeyPadQueryHotSpot?:    (id: number) => number;
    LCDSetWindow?:          (mode: number, y: number, w: number, h: number) => void;
    SetSigWindow?:          (mode: number, x: number, y: number, w: number, h: number) => void;
    LCDStringWidth?:        (font: string, text: string) => number;
  }
}

const BTN_FONT  = "9pt Arial";
const LINE_H    = 15;

const HOTSPOT = { CANCEL: 0, AGREE: 1, CLEAR: 2, OK: 3 };

type Step =
  | "idle"
  | "checking"
  | "unavailable"
  | "agreement"
  | "cancelled"
  | "signing"
  | "captured";

const DEFAULT_AGREEMENT_TEXT =
  "These are sample terms and conditions for the Topaz pad test. Please review on the pad, then press Cancel or Agree.";

const TopazTestPage: React.FC = () => {
  const sigWeb = useSigWeb();

  const [step, setStep] = useState<Step>("idle");
  const [agreementText, setAgreementText] = useState(DEFAULT_AGREEMENT_TEXT);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);

  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const tmrRef         = useRef<number | undefined>(undefined);
  const eventTmrRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const lcdSizeRef     = useRef({ w: LCD_W, h: LCD_H });

  // ── Helpers ────────────────────────────────────────────────────────────────

  const clearInkCanvas = useCallback(() => {
    const cv = canvasRef.current;
    cv?.getContext("2d")?.clearRect(0, 0, cv.width, cv.height);
  }, []);

  const stopTabletLoop = useCallback(() => {
    window.onSigPenUp = null;
    if (eventTmrRef.current !== null) {
      clearInterval(eventTmrRef.current);
      eventTmrRef.current = null;
    }
    try {
      window.SetTabletState?.(0, null, tmrRef.current as unknown as number);
    } catch {
      try { window.SetTabletState?.(0); } catch {}
    }
    tmrRef.current = undefined;
  }, []);

  const writeWrappedText = useCallback((text: string, maxY: number) => {
    const { w } = lcdSizeRef.current;
    const words = text.trim().split(/\s+/);
    let line = "";
    let y = 2;
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      const width = window.LCDStringWidth?.(BTN_FONT, candidate) ?? candidate.length * 6;
      if (width > w - 4 && line) {
        window.LCDWriteString?.(0, 2, 2, y, BTN_FONT, LINE_H, line);
        y += LINE_H;
        line = word;
        if (y + LINE_H > maxY) return;
      } else {
        line = candidate;
      }
    }
    if (line && y + LINE_H <= maxY) {
      window.LCDWriteString?.(0, 2, 2, y, BTN_FONT, LINE_H, line);
    }
  }, []);

  // ── Pad screens ──────────────────────────────────────────────────────────

  const showAgreementScreen = useCallback(() => {
    const { w, h } = lcdSizeRef.current;
    window.ClearTablet?.();
    window.LCDClear?.();
    window.LcdRefresh?.(0, 0, 0, w, h);

    const btnH  = Math.max(14, Math.round(h * 0.234));
    const btnY  = h - btnH - Math.round(h * 0.07);

    writeWrappedText(agreementText, btnY - 4);

    const cancelW = (window.LCDStringWidth?.(BTN_FONT, "Cancel") ?? 30) + 10;
    const agreeW  = (window.LCDStringWidth?.(BTN_FONT, "Agree")  ?? 30) + 10;
    const cancelX = 4;
    const agreeX  = w - agreeW - 4;

    window.LCDWriteString?.(0, 2, cancelX + 5, btnY + 4, BTN_FONT, LINE_H, "Cancel");
    window.LCDWriteString?.(0, 2, agreeX + 5,  btnY + 4, BTN_FONT, LINE_H, "Agree");

    window.KeyPadClearHotSpotList?.();
    window.KeyPadAddHotSpot?.(HOTSPOT.CANCEL, 1, cancelX, btnY, cancelW, btnH);
    window.KeyPadAddHotSpot?.(HOTSPOT.AGREE,  1, agreeX,  btnY, agreeW,  btnH);

    window.SetLCDCaptureMode?.(2);
    window.ClearTablet?.();

    window.onSigPenUp = () => {
      if ((window.KeyPadQueryHotSpot?.(HOTSPOT.CANCEL) ?? 0) > 0) { onCancel(); return; }
      if ((window.KeyPadQueryHotSpot?.(HOTSPOT.AGREE)  ?? 0) > 0) { showCaptureScreen(); return; }
    };

    setStep("agreement");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementText, writeWrappedText]);

  const showCaptureScreen = useCallback(() => {
    const { w, h } = lcdSizeRef.current;
    window.ClearTablet?.();
    window.LCDClear?.();
    window.LcdRefresh?.(0, 0, 0, w, h);
    clearInkCanvas();

    window.LCDWriteString?.(0, 2, 2, 2, BTN_FONT, LINE_H, "Sign below, then OK");

    const btnH = Math.max(14, Math.round(h * 0.27));
    const btnY = Math.round(h * 0.08) + LINE_H;

    const clearW = (window.LCDStringWidth?.(BTN_FONT, "Clear") ?? 40) + 10;
    const okW    = (window.LCDStringWidth?.(BTN_FONT, "OK")    ?? 24) + 10;
    const clearX = 4;
    const okX    = w - okW - 4;

    window.LCDWriteString?.(0, 2, clearX + 5, btnY + 4, BTN_FONT, LINE_H, "Clear");
    window.LCDWriteString?.(0, 2, okX + 5,    btnY + 4, BTN_FONT, LINE_H, "OK");

    window.KeyPadClearHotSpotList?.();
    window.KeyPadAddHotSpot?.(HOTSPOT.CLEAR, 1, clearX, btnY, clearW, btnH);
    window.KeyPadAddHotSpot?.(HOTSPOT.OK,    1, okX,    btnY, okW,    btnH);

    const sigY = btnY + btnH + 4;
    const sigH = Math.max(20, h - sigY - 2);

    window.LCDSetWindow?.(2, sigY, w, sigH);
    window.SetSigWindow?.(1, 0, sigY, w, sigH);

    window.ClearTablet?.();
    window.SetLCDCaptureMode?.(2);

    window.onSigPenUp = () => {
      if ((window.KeyPadQueryHotSpot?.(HOTSPOT.CLEAR) ?? 0) > 0) {
        window.ClearTablet?.();
        clearInkCanvas();
        return;
      }
      if ((window.KeyPadQueryHotSpot?.(HOTSPOT.OK) ?? 0) > 0) {
        if ((window.NumberOfTabletPoints?.() ?? 0) === 0) return;
        finishCapture();
      }
    };

    setStep("signing");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearInkCanvas]);

  const onCancel = useCallback(() => {
    const { w, h } = lcdSizeRef.current;
    window.KeyPadClearHotSpotList?.();
    window.ClearTablet?.();
    window.LCDClear?.();
    window.LCDWriteString?.(0, 2, Math.max(4, Math.round(w / 2) - 30), Math.round(h / 2) - 8, BTN_FONT, LINE_H, "Cancelled");
    window.LcdRefresh?.(0, 0, 0, w, h);
    stopTabletLoop();
    setStep("cancelled");
  }, [stopTabletLoop]);

  const finishCapture = useCallback(() => {
    const { w, h } = lcdSizeRef.current;
    const img = sigWeb.getImage();
    window.KeyPadClearHotSpotList?.();
    window.LCDClear?.();
    window.LCDWriteString?.(0, 2, Math.max(4, Math.round(w / 2) - 55), Math.round(h / 2) - 8, BTN_FONT, LINE_H, "Signature captured");
    window.LcdRefresh?.(0, 0, 0, w, h);
    stopTabletLoop();
    setSignatureImage(img);
    setStep("captured");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopTabletLoop]);

  // ── Start / restart ─────────────────────────────────────────────────────

  const handleStart = useCallback(async () => {
    setSignatureImage(null);
    setStep("checking");

    const available = await sigWeb.check();
    if (!available) { setStep("unavailable"); return; }

    try {
      const canvas = canvasRef.current;
      const ctx    = canvas?.getContext("2d") ?? null;

      if (eventTmrRef.current !== null) clearInterval(eventTmrRef.current);
      eventTmrRef.current = setInterval(() => window.SigWebEvent?.(), 20);
      tmrRef.current = window.SetTabletState?.(1, ctx, 50);

      window.SetJustifyMode?.(0);
      window.SetDisplayXSize?.(SIGWEB_CANVAS_W);
      window.SetDisplayYSize?.(SIGWEB_CANVAS_H);
      window.SetImageXSize?.(SIGWEB_CANVAS_W);
      window.SetImageYSize?.(SIGWEB_CANVAS_H);

      const rawSize = Number(window.LCDGetLCDSize?.());
      lcdSizeRef.current = rawSize
        ? { w: rawSize & 0xffff, h: (rawSize >> 16) & 0xffff }
        : { w: LCD_W, h: LCD_H };

      showAgreementScreen();
    } catch {
      setStep("unavailable");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAgreementScreen]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      window.onSigPenUp = null;
      if (eventTmrRef.current !== null) clearInterval(eventTmrRef.current);
      try { window.SetTabletState?.(0, null, tmrRef.current as unknown as number); } catch {}
      window.KeyPadClearHotSpotList?.();
      window.SetLCDCaptureMode?.(1);
      window.LCDClear?.();
      window.ClearTablet?.();
    };
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  const statusLabel: Record<Step, string> = {
    idle:        "Not started",
    checking:    "Connecting to pad…",
    unavailable: "SigWeb not detected",
    agreement:   "Awaiting response on pad (Cancel / Agree)",
    cancelled:   "Customer pressed Cancel on the pad",
    signing:     "Awaiting signature on pad (Clear / OK)",
    captured:    "Signature captured",
  };

  return (
    <PosLayout>
      <Box sx={{ p: "1.5rem", maxWidth: 720 }}>
        <Typography fontWeight="bold" fontSize="var(--fs-lg)" color={Colors.TEXT_GRAY_DARK} mb={0.5}>
          Topaz Pad Test
        </Typography>
        <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY} mb={2}>
          Pushes text and two on-pad buttons (Cancel / Agree) to the Topaz LCD, then captures
          a signature once the customer agrees and shows it below.
        </Typography>

        <CustomTextBox
          multiline
          rows={4}
          name="agreement_text"
          value={agreementText}
          onChange={(e) => setAgreementText(e.target.value)}
          disabled={step !== "idle" && step !== "unavailable" && step !== "cancelled" && step !== "captured"}
          sx={{ width: "100%", mb: 1.5 }}
          placeholder="Text to display on the Topaz LCD"
        />

        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <CustomButton
            variant="contained"
            onClick={handleStart}
            disabled={step === "checking" || step === "agreement" || step === "signing"}
            sx={{
              backgroundColor: Colors.PRIMARY, color: Colors.WHITE,
              borderRadius: "5px", px: 3, py: 0.8, fontSize: "var(--fs-base)",
              "&:hover": { backgroundColor: Colors.PRIMARY_DARK },
            }}
          >
            {step === "idle" ? "Send to Pad" : "Restart Test"}
          </CustomButton>

          <Box display="flex" alignItems="center" gap={1}>
            {(step === "checking") && <CircularProgress size={18} sx={{ color: Colors.PRIMARY }} />}
            <Typography fontSize="var(--fs-base)" color={Colors.MAIN_GRAY}>
              {statusLabel[step]}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            border: "1.5px solid #e0e0e0",
            borderRadius: "8px",
            bgcolor: Colors.BACKSHADE_GRAY,
            p: 1.5,
            mb: 2,
          }}
        >
          <Typography fontSize="var(--fs-xs)" color={Colors.MAIN_GRAY} mb={1}>
            Live ink preview
          </Typography>
          <canvas
            ref={canvasRef}
            width={SIGWEB_CANVAS_W}
            height={SIGWEB_CANVAS_H}
            style={{ display: "block", width: "100%", height: "100px", background: Colors.WHITE, borderRadius: "6px" }}
          />
        </Box>

        {signatureImage && (
          <Box
            sx={{
              border: `1.5px solid ${Colors.PRIMARY}`,
              borderRadius: "8px",
              bgcolor: Colors.WHITE,
              p: 1.5,
            }}
          >
            <Typography fontWeight="bold" fontSize="var(--fs-base)" color={Colors.PRIMARY} mb={1}>
              Signature captured
            </Typography>
            <img
              src={signatureImage}
              alt="Captured signature"
              style={{ maxWidth: "100%", display: "block" }}
            />
          </Box>
        )}
      </Box>
    </PosLayout>
  );
};

export default TopazTestPage;
